import axios from "axios";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { API_BASE_URL } from "../../config";
import { toast } from "react-toastify";

const UpsertCustomerRole = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [menuList, setMenuList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMenus, setSelectedMenus] = useState({});
  const [roleName, setRoleName] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [roleData, setRoleData] = useState([]);
  const [selectedRoleLevels, setSelectedRoleLevels] = useState([]);

  const permissions = [
    { key: "can_view", label: "View", icon: "fa fa-eye" },
    { key: "can_create", label: "Add", icon: "fa fa-plus" },
    { key: "can_edit", label: "Edit", icon: "fa fa-pencil" },
    { key: "can_delete", label: "Delete", icon: "fa fa-trash" },
    { key: "import_export", label: "Import/Export", icon: "fa fa-exchange" },
  ];

  useEffect(() => {
    fetchMenuList();
    fetchRoleData();
  }, []);

  useEffect(() => {
    if (isEditMode && menuList.length > 0) {
      fetchRoleDetails();
    }
  }, [id, isEditMode, menuList]);

  const fetchRoleDetails = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/get-role-based-menu/${id}`
      );
      const roleDetails = response.data;
      console.log(roleDetails);

      // Set role name
      setRoleName(roleDetails?.role?.role_name);

      // Set role levels
      const roleLevels = roleDetails?.role?.reporting
        ? roleDetails?.role?.reporting
            ?.split(",")
            .map((level) => parseInt(level.trim()))
        : [];
      setSelectedRoleLevels(roleLevels);

      // Initialize selected menus with fetched data
      const newSelectedMenus = {};

      // First, initialize all menus from menuList
      menuList?.forEach((menu) => {
        const initialPermissions = {};
        permissions.forEach((perm) => {
          initialPermissions[perm.key] = 0;
        });

        newSelectedMenus[menu.menu_id] = {
          selected: false,
          permissions: { ...initialPermissions },
          subMenus: {},
        };

        if (menu.sub_menus && menu.sub_menus.length > 0) {
          menu.sub_menus.forEach((subMenu) => {
            newSelectedMenus[menu.menu_id].subMenus[subMenu.sub_menu_id] = {
              selected: false,
              permissions: { ...initialPermissions },
            };
          });
        }
      });

      // Then, update with actual role data
      roleDetails?.menu_access?.forEach((menu) => {
        if (newSelectedMenus[menu.menu_id]) {
          // Select the menu since it's present in the API response
          newSelectedMenus[menu.menu_id].selected = true;

          // Set menu permissions from the first element of menu_permition array
          if (menu.menu_permition && menu.menu_permition[0]) {
            Object.keys(menu.menu_permition[0]).forEach((key) => {
              if (permissions.some((p) => p.key === key)) {
                newSelectedMenus[menu.menu_id].permissions[key] =
                  menu.menu_permition[0][key];
              }
            });
          }

          // Process submenus
          if (menu.sub_menus && menu.sub_menus.length > 0) {
            menu.sub_menus.forEach((subMenuData) => {
              if (
                newSelectedMenus[menu.menu_id].subMenus[subMenuData.sub_menu_id]
              ) {
                // Select the submenu since it's present in the API response
                newSelectedMenus[menu.menu_id].subMenus[
                  subMenuData.sub_menu_id
                ].selected = true;

                // Set submenu permissions from the first element of sub_menu_permition array
                if (
                  subMenuData.sub_menu_permition &&
                  subMenuData.sub_menu_permition[0]
                ) {
                  Object.keys(subMenuData.sub_menu_permition[0]).forEach(
                    (key) => {
                      if (permissions.some((p) => p.key === key)) {
                        newSelectedMenus[menu.menu_id].subMenus[
                          subMenuData.sub_menu_id
                        ].permissions[key] =
                          subMenuData.sub_menu_permition[0][key];
                      }
                    }
                  );
                }
              }
            });
          }
        }
      });

      setSelectedMenus(newSelectedMenus);
    } catch (error) {
      console.error("Error fetching role details:", error);
      // toast.error("Failed to load role details. Please try again.");
    }
  };

  const fetchRoleData = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/get-role-list`);
      const filteredRoles = isEditMode
        ? response.data.data.filter((role) => role.id !== parseInt(id))
        : response.data.data;
      setRoleData(filteredRoles);
    } catch (error) {
      console.error("Error fetching role data:", error);
    }
  };

  const fetchMenuList = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/get-menu-list`, {
        headers: { "Content-Type": "application/json" },
      });
      setMenuList(response.data.data);

      // Initialize selected menus state
      const initialSelectedState = {};
      response.data.data.forEach((menu) => {
        const initialPermissions = {};
        permissions.forEach((perm) => {
          initialPermissions[perm.key] = 0;
        });

        initialSelectedState[menu.menu_id] = {
          selected: false,
          permissions: { ...initialPermissions },
          subMenus: {},
        };

        if (menu.sub_menus && menu.sub_menus.length > 0) {
          menu.sub_menus.forEach((subMenu) => {
            initialSelectedState[menu.menu_id].subMenus[subMenu.sub_menu_id] = {
              selected: false,
              permissions: { ...initialPermissions },
            };
          });
        }
      });

      setSelectedMenus(initialSelectedState);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching menu data:", error);
      toast.error("Failed to load menu data. Please try again.");
      setIsLoading(false);
    }
  };

  const handleMenuChange = (menuId) => {
    setSelectedMenus((prevState) => {
      const menuIdNum = Number(menuId);
      const newState = JSON.parse(JSON.stringify(prevState)); // Deep clone

      const newSelectedState = !newState[menuIdNum].selected;
      newState[menuIdNum].selected = newSelectedState;

      // If menu is deselected, deselect all its sub-menus
      if (!newSelectedState && newState[menuIdNum].subMenus) {
        Object.keys(newState[menuIdNum].subMenus).forEach((subMenuId) => {
          newState[menuIdNum].subMenus[subMenuId].selected = false;
        });
      }

      return newState;
    });
  };

  const handleSubMenuChange = (menuId, subMenuId) => {
    setSelectedMenus((prevState) => {
      const menuIdNum = Number(menuId);
      const subMenuIdNum = Number(subMenuId);
      const newState = JSON.parse(JSON.stringify(prevState)); // Deep clone

      // Toggle the selected state of the sub-menu
      const newSelectedState =
        !newState[menuIdNum].subMenus[subMenuIdNum].selected;
      newState[menuIdNum].subMenus[subMenuIdNum].selected = newSelectedState;

      // Ensure parent menu is selected if any submenu is selected
      if (
        newSelectedState ||
        Object.values(newState[menuIdNum].subMenus).some((sm) => sm.selected)
      ) {
        newState[menuIdNum].selected = true;
      }

      return newState;
    });
  };

  const handlePermissionChange = (menuId, permissionKey, subMenuId = null) => {
    console.log(
      `handlePermissionChange called - menuId: ${menuId}, permissionKey: ${permissionKey}, subMenuId: ${subMenuId}`
    );

    setSelectedMenus((prevState) => {
      const menuIdNum = Number(menuId);
      const newState = JSON.parse(JSON.stringify(prevState)); // Deep clone to avoid mutations

      if (subMenuId) {
        const subMenuIdNum = Number(subMenuId);
        const currentValue =
          newState[menuIdNum].subMenus[subMenuIdNum].permissions[permissionKey];
        console.log(`Current submenu permission value: ${currentValue}`);
        newState[menuIdNum].subMenus[subMenuIdNum].permissions[permissionKey] =
          currentValue === 1 ? 0 : 1;
        console.log(
          `New submenu permission value: ${newState[menuIdNum].subMenus[subMenuIdNum].permissions[permissionKey]}`
        );
      } else {
        const currentValue = newState[menuIdNum].permissions[permissionKey];
        console.log(`Current menu permission value: ${currentValue}`);
        newState[menuIdNum].permissions[permissionKey] =
          currentValue === 1 ? 0 : 1;
        console.log(
          `New menu permission value: ${newState[menuIdNum].permissions[permissionKey]}`
        );
      }

      console.log("New state:", newState);
      return newState;
    });
  };

  const handleRoleLevelChange = (roleId) => {
    setSelectedRoleLevels((prevLevels) => {
      if (prevLevels.includes(roleId)) {
        return prevLevels.filter((id) => id !== roleId);
      } else {
        return [...prevLevels, roleId];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const errors = {};
    if (!roleName.trim()) {
      errors.roleName = "Role name is required";
    }

    const hasSelectedMenus = Object.values(selectedMenus).some(
      (menu) => menu.selected
    );
    if (!hasSelectedMenus) {
      errors.menus = "At least one menu must be selected";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    // Prepare payload
    const menuAccess = [];
    Object.entries(selectedMenus).forEach(([menuId, menuData]) => {
      if (menuData.selected) {
        const menuItem = {
          menu_id: parseInt(menuId),
          sub_menu_ids: [],
        };

        // Check if menu has submenus
        const hasSelectedSubmenus = Object.values(menuData.subMenus).some(
          (sm) => sm.selected
        );

        if (!hasSelectedSubmenus) {
          // If no submenus selected, add permissions to the menu itself
          menuItem.permissions = menuData.permissions;
        } else {
          // If submenus selected, add permissions to each submenu
          Object.entries(menuData.subMenus).forEach(
            ([subMenuId, subMenuData]) => {
              if (subMenuData.selected) {
                menuItem.sub_menu_ids.push({
                  sub_menu_id: parseInt(subMenuId),
                  permissions: subMenuData.permissions,
                });
              }
            }
          );
        }

        menuAccess.push(menuItem);
      }
    });

    const payload = isEditMode
      ? {
          role: roleName,
          role_id: id,
          role_level: selectedRoleLevels.join(","),
          menu_access: menuAccess,
        }
      : {
          role: roleName,
          role_level: selectedRoleLevels.join(","),
          menu_access: menuAccess,
        };

    // console.log("Submitting payload:", payload);

    setIsSubmitting(true);
    setIsLoading(true);
    try {
      const endpoint = isEditMode
        ? `${API_BASE_URL}/update-role`
        : `${API_BASE_URL}/add-role`;
      const method = isEditMode ? "post" : "post";

      await axios[method](endpoint, payload, {
        headers: { "Content-Type": "application/json" },
      });

      toast.success(`Role ${isEditMode ? "updated" : "added"} successfully!`);
      navigate("/cms/manage-customer-role");
    } catch (error) {
      console.error(`Error ${isEditMode ? "updating" : "adding"} role:`, error);
      toast.error(
        `Failed to ${isEditMode ? "update" : "add"} role. Please try again.`
      );
    } finally {
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  const renderPermissionCheckboxes = (menuId, subMenuId = null) => {
    const menuData = selectedMenus[menuId];
    if (!menuData) return null;

    const permissionData = subMenuId
      ? menuData.subMenus[subMenuId]?.permissions || {}
      : menuData.permissions || {};

    return (
      <div className="permission-checkboxes d-flex gap-3 ms-4 mt-2">
        {permissions.map((perm) => (
          <div key={perm.key} className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id={`perm-${menuId}-${subMenuId || "menu"}-${perm.key}`}
              checked={permissionData[perm.key] === 1}
              onChange={(e) => {
                // e.preventDefault();
                handlePermissionChange(menuId, perm.key, subMenuId);
              }}
            />
            <label
              className="form-check-label small"
              htmlFor={`perm-${menuId}-${subMenuId || "menu"}-${perm.key}`}
            >
              <i className={`${perm.icon} me-1`}></i>
              {perm.label}
            </label>
          </div>
        ))}
      </div>
    );
  };

  const renderMenuItems = () => {
    return menuList.map((menu) => {
      const menuState = selectedMenus[menu.menu_id] || {
        selected: false,
        permissions: {},
        subMenus: {},
      };
      const hasSubMenus = menu.sub_menus && menu.sub_menus.length > 0;

      return (
        <div key={menu.menu_id} className="menu-item mb-3 border-bottom pb-3">
          <div className="d-flex flex-column">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id={`menu-${menu.menu_id}`}
                checked={menuState.selected}
                onChange={() => handleMenuChange(menu.menu_id)}
              />
              <label
                className="form-check-label fw-bold"
                htmlFor={`menu-${menu.menu_id}`}
              >
                <i className={`${menu.menu_icon} me-2`}></i>
                {menu.menu_name}
              </label>
            </div>

            {/* Show permissions for menu if it doesn't have submenus and is selected */}
            {!hasSubMenus &&
              menuState.selected &&
              renderPermissionCheckboxes(menu.menu_id)}
          </div>

          {/* Render sub-menus if they exist */}
          {hasSubMenus && (
            <div className="sub-menu-list ms-4 mt-2">
              {menu.sub_menus.map((subMenu) => {
                const subMenuData = menuState.subMenus[subMenu.sub_menu_id] || {
                  selected: false,
                  permissions: {},
                };

                return (
                  <div key={subMenu.sub_menu_id} className="sub-menu-item mb-3">
                    <div className="d-flex flex-column">
                      <div className="form-check mb-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`submenu-${subMenu.sub_menu_id}`}
                          checked={subMenuData.selected}
                          onChange={() =>
                            handleSubMenuChange(
                              menu.menu_id,
                              subMenu.sub_menu_id
                            )
                          }
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`submenu-${subMenu.sub_menu_id}`}
                        >
                          <i className={`${subMenu.sub_menu_icon} me-2`}></i>
                          {subMenu.sub_menu_name}
                        </label>
                      </div>
                      {/* Show permissions for submenu if it's selected */}
                      {subMenuData.selected &&
                        renderPermissionCheckboxes(
                          menu.menu_id,
                          subMenu.sub_menu_id
                        )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    });
  };

  const renderRoleLevels = () => {
    return (
      <div className="form-group row mb-4">
        <label className="col-form-label col-lg-2">Role Level</label>
        <div className="col-lg-10">
          <div className="role-levels d-flex flex-wrap gap-3">
            {roleData.map((role) => (
              <div key={role.id} className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`role-${role.id}`}
                  checked={selectedRoleLevels.includes(role.id)}
                  onChange={() => handleRoleLevelChange(role.id)}
                />
                <label className="form-check-label" htmlFor={`role-${role.id}`}>
                  {role.role_name}
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{`
        .permission-checkboxes {
          background-color: #f8f9fa;
          padding: 10px;
          border-radius: 5px;
          margin-bottom: 10px;
        }
        .permission-checkboxes .form-check {
          margin-bottom: 0;
        }
        .menu-item {
          position: relative;
        }
        .sub-menu-item {
          position: relative;
        }
          .permission-checkboxes input[type="checkbox"] {
            pointer-events: auto;
            isolation: isolate;
            }
      `}</style>
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 className="mb-sm-0 font-size-18">
                    {isEditMode ? "Edit" : "Add"} Customer Role
                  </h4>

                  <div className="page-title-right">
                    <ol className="breadcrumb m-0">
                      <li className="breadcrumb-item">
                        <a href="javascript: void(0);">CMS</a>
                      </li>
                      <li className="breadcrumb-item">
                        <Link to={"/cms/manage-customer-role"}>
                          Manage Customer Role
                        </Link>
                      </li>
                      <li className="breadcrumb-item active">
                        {isEditMode ? "Edit" : "Add"} Customer Role
                      </li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-12">
              <div className="row">
                <div className="col-lg-12">
                  <div className="card">
                    <div className="card-body">
                      <h4 className="card-title mb-4">
                        {isEditMode ? "Edit" : "Add"} Role
                      </h4>
                      <form onSubmit={handleSubmit}>
                        <div data-repeater-list="outer-group" className="outer">
                          <div data-repeater-item className="outer">
                            <div className="form-group row mb-4">
                              <label
                                htmlFor="roleName"
                                className="col-form-label col-lg-2"
                              >
                                Role Name <span className="text-danger">*</span>
                              </label>
                              <div className="col-lg-4">
                                <input
                                  id="roleName"
                                  name="roleName"
                                  type="text"
                                  className={`form-control ${
                                    formErrors.roleName ? "is-invalid" : ""
                                  }`}
                                  placeholder="Enter Role Name..."
                                  value={roleName}
                                  onChange={(e) => setRoleName(e.target.value)}
                                />
                                {formErrors.roleName && (
                                  <div className="invalid-feedback">
                                    {formErrors.roleName}
                                  </div>
                                )}
                              </div>
                            </div>
                            {/* Role Levels */}
                            {renderRoleLevels()}
                            <div className="form-group row mb-4">
                              <label className="col-form-label col-lg-2">
                                Menu Access & Permissions{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <div className="col-lg-10">
                                {isLoading ? (
                                  <div className="text-center">
                                    <div
                                      className="spinner-border text-primary"
                                      role="status"
                                    >
                                      <span className="visually-hidden">
                                        Loading...
                                      </span>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="menu-selection-container border p-3 rounded">
                                    {menuList.length > 0 ? (
                                      <>
                                        {renderMenuItems()}
                                        {formErrors.menus && (
                                          <div className="text-danger mt-2">
                                            {formErrors.menus}
                                          </div>
                                        )}
                                      </>
                                    ) : (
                                      <div className="alert alert-warning">
                                        No menus available. Please check the
                                        system configuration.
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="row justify-content-end">
                          <div className="col-lg-10">
                            <button
                              type="submit"
                              className="btn btn-primary me-2"
                              disabled={isSubmitting}
                            >
                              {isSubmitting ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-1"
                                    role="status"
                                    aria-hidden="true"
                                  ></span>
                                  Processing...
                                </>
                              ) : isEditMode ? (
                                "Update Role"
                              ) : (
                                "Add Role"
                              )}
                            </button>
                            <Link
                              to="/cms/manage-customer-role"
                              className="btn btn-secondary"
                            >
                              Cancel
                            </Link>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UpsertCustomerRole;
